/* ============================================================================
   Delta. Signo + flecha + color: tres canales para una sola lectura, así que el
   color nunca es el único portador del sentido (requisito para daltonismo).

   Convención de esta app: que el precio SUBA es adverso para la seguridad
   alimentaria. `invert` sirve para métricas donde subir es favorable
   (p. ej. canastas que alcanza un salario).
   ========================================================================== */

import { deltaArrow, percentDelta, currencyDelta } from '../lib/format'
import './delta.css'

export type Tone = 'adverse' | 'favorable' | 'neutral'

export function toneOf(value: number, invert = false, threshold = 0): Tone {
  if (Math.abs(value) <= threshold || value === 0) return 'neutral'
  const rising = value > 0
  const good = invert ? rising : !rising
  return good ? 'favorable' : 'adverse'
}

export interface DeltaProps {
  value: number
  kind?: 'percent' | 'currency'
  /** Sufijo descriptivo: «vs. mes anterior». */
  since?: string
  /** Subir es favorable. */
  invert?: boolean
  size?: 'sm' | 'md'
  decimals?: number
}

export function Delta({
  value,
  kind = 'percent',
  since,
  invert = false,
  size = 'sm',
  decimals = 1,
}: DeltaProps) {
  /*
     La flecha, el signo, el color y la etiqueta para lector de pantalla se
     derivan todos del valor YA REDONDEADO a los decimales que se imprimen. Sin
     esto, un +0.04 % se imprimía «0.0 %» con flecha hacia arriba y color de
     aumento: cuatro canales diciendo tres cosas distintas.
  */
  const rounded = Number(value.toFixed(decimals))
  const tone = toneOf(rounded, invert)
  const text = kind === 'percent' ? percentDelta(rounded, decimals) : currencyDelta(rounded, decimals)
  const label = rounded === 0 ? 'sin cambio' : rounded > 0 ? 'aumento' : 'disminución'

  return (
    <span className={`delta delta--${tone} delta--${size}`}>
      <span className="delta__arrow" aria-hidden="true">
        {deltaArrow(rounded)}
      </span>
      <span className="delta__value tabular">{text}</span>
      <span className="sr-only">{label}</span>
      {since ? <span className="delta__since t-caption ink-3">{since}</span> : null}
    </span>
  )
}
